import { LocalDate } from "@js-joda/core";
import { type CustomSale } from "./analytics.customSales";

// ─── CSV parsing ────────────────────────────────────────────────────────────
//
// Deliberately hand-rolled rather than pulling in a library: the format this
// supports is narrow (two columns, no embedded newlines expected), and a
// small parser is easier to keep predictable across the Swedish-export quirks
// below (semicolon delimiters, comma decimals) than configuring a general one.

const splitLines = (text: string): string[] =>
  text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

/**
 * Swedish spreadsheet exports typically use ";" (since "," is the decimal
 * separator locally); everything else tends to use ",". Pick whichever
 * appears consistently across the sampled lines.
 */
const detectDelimiter = (lines: string[]): "," | ";" => {
  const sample = lines.slice(0, 10);
  const semicolons = sample.filter((l) => l.includes(";")).length;
  const commas = sample.filter((l) => l.includes(",")).length;
  return semicolons >= commas ? ";" : ",";
};

/** Splits one line on the delimiter, honouring simple "quoted, fields". */
const splitLine = (line: string, delimiter: "," | ";"): string[] => {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
};

export interface ParsedCsv {
  /** Header cell text if the first line looks like a header, else null. */
  headers: string[] | null;
  /** Every remaining line, split into cells. */
  rows: string[][];
}

/**
 * Parses raw CSV text into a header (if present) and data rows.
 *
 * A header is detected heuristically: if the first line's cells don't parse
 * as a valid date and a valid amount, it's assumed to be a header rather than
 * a malformed data row.
 */
export const parseCsv = (text: string): ParsedCsv => {
  const lines = splitLines(text);
  if (lines.length === 0) return { headers: null, rows: [] };

  const delimiter = detectDelimiter(lines);
  const allRows = lines.map((line) => splitLine(line, delimiter));

  const [first, ...rest] = allRows;
  const looksLikeData =
    first.length >= 2 &&
    parseImportDate(first[0]) !== null &&
    parseImportAmount(first[1]) !== null;

  return looksLikeData
    ? { headers: null, rows: allRows }
    : { headers: first, rows: rest };
};

// ─── Field parsing ──────────────────────────────────────────────────────────

/**
 * Accepts ISO (2026-08-13), and the app's own DD/MM/YYYY or DD-MM-YYYY —
 * matching the format the DatePicker already displays elsewhere on this page.
 */
export const parseImportDate = (raw: string): LocalDate | null => {
  const value = raw.trim();
  if (!value) return null;

  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) return tryLocalDate(+iso[1], +iso[2], +iso[3]);

  const dmy = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) return tryLocalDate(+dmy[3], +dmy[2], +dmy[1]);

  return null;
};

const tryLocalDate = (
  year: number,
  month: number,
  day: number,
): LocalDate | null => {
  try {
    return LocalDate.of(year, month, day);
  } catch {
    return null;
  }
};

/**
 * Accepts a plain number, a Swedish-style decimal comma, thousand separators
 * (space or non-breaking space, as Swedish exports use), and a trailing "kr".
 */
// Strict: the whole cleaned string must be a number, nothing more.
// parseFloat on its own reads a leading numeric prefix and silently
// ignores the rest (e.g. "500foo" -> 500), which could corrupt sales
// data from a malformed export instead of flagging the row as invalid.
const STRICT_NUMBER = /^-?\d+(\.\d+)?$/;

export const parseImportAmount = (raw: string): number | null => {
  const cleaned = raw
    .trim()
    .replace(/\s*kr\.?$/i, "")
    .replace(/[\s ]/g, "")
    .replace(",", ".");
  if (!cleaned || !STRICT_NUMBER.test(cleaned)) return null;
  const amount = parseFloat(cleaned);
  return Number.isFinite(amount) ? amount : null;
};

/**
 * Guesses which columns hold the date and the amount, by counting how many
 * sampled rows parse successfully as each in every column. Falls back to
 * {0, 1} — the order the feature was described in — when nothing stands out.
 */
export const guessColumns = (
  rows: string[][],
): { dateColumnIndex: number; amountColumnIndex: number } => {
  const columnCount = rows.reduce((max, r) => Math.max(max, r.length), 0);
  if (columnCount < 2) return { dateColumnIndex: 0, amountColumnIndex: 1 };

  const sample = rows.slice(0, 20);
  const dateScores: number[] = [];
  const amountScores: number[] = [];
  for (let col = 0; col < columnCount; col++) {
    dateScores.push(
      sample.filter((r) => parseImportDate(r[col] ?? "") !== null).length,
    );
    amountScores.push(
      sample.filter((r) => parseImportAmount(r[col] ?? "") !== null).length,
    );
  }

  const bestDate = dateScores.indexOf(Math.max(...dateScores));

  // Pick the strongest amount column that isn't the one already claimed by
  // the date guess — a valid date string can also parse as a plain number.
  let bestAmount = bestDate === 0 ? 1 : 0;
  for (let col = 0; col < columnCount; col++) {
    if (col !== bestDate && amountScores[col] > amountScores[bestAmount]) {
      bestAmount = col;
    }
  }

  return dateScores[bestDate] > 0
    ? { dateColumnIndex: bestDate, amountColumnIndex: bestAmount }
    : { dateColumnIndex: 0, amountColumnIndex: 1 };
};

// ─── Row-level preview ────────────────────────────────────────────────────────

export type ImportRowError = "invalid-date" | "invalid-amount" | "not-positive";

export interface ParsedImportRow {
  /** 1-based, counting only data rows — for user-facing row references. */
  rowNumber: number;
  rawDate: string;
  rawAmount: string;
  date: LocalDate | null;
  amount: number | null;
  error: ImportRowError | null;
}

/** Parses every data row against the chosen column mapping. */
export const buildImportRows = (
  rows: string[][],
  dateColumnIndex: number,
  amountColumnIndex: number,
): ParsedImportRow[] =>
  rows.map((cells, i) => {
    const rawDate = cells[dateColumnIndex] ?? "";
    const rawAmount = cells[amountColumnIndex] ?? "";
    const date = parseImportDate(rawDate);
    const amount = parseImportAmount(rawAmount);

    let error: ImportRowError | null = null;
    if (date === null) error = "invalid-date";
    else if (amount === null) error = "invalid-amount";
    else if (amount <= 0) error = "not-positive";

    return { rowNumber: i + 1, rawDate, rawAmount, date, amount, error };
  });

// ─── Conflict summary ─────────────────────────────────────────────────────────

export interface ImportDateConflict {
  /** ISO "YYYY-MM-DD" */
  date: string;
  /** Sum of every valid CSV row for this date — rows on the same date add together. */
  importedAmount: number;
  /** Sum of custom sales already recorded for this date, before the import. */
  existingAmount: number;
}

/**
 * For every date the import would touch, compares what's already recorded
 * against what's being added. The import is always additive (never a
 * replace), so this is purely informational — surfaced as a warning before
 * the user confirms, rather than a choice they have to make per date.
 */
export const buildImportConflicts = (
  rows: ParsedImportRow[],
  existingSales: CustomSale[],
): ImportDateConflict[] => {
  const importedByDate = new Map<string, number>();
  for (const row of rows) {
    if (row.error || !row.date || row.amount === null) continue;
    const key = row.date.toString();
    importedByDate.set(key, (importedByDate.get(key) ?? 0) + row.amount);
  }

  const existingByDate = new Map<string, number>();
  for (const sale of existingSales) {
    existingByDate.set(
      sale.date,
      (existingByDate.get(sale.date) ?? 0) + sale.amount,
    );
  }

  return Array.from(importedByDate.entries())
    .map(([date, importedAmount]) => ({
      date,
      importedAmount,
      existingAmount: existingByDate.get(date) ?? 0,
    }))
    .filter((c) => c.existingAmount > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
};

// ─── Commit ───────────────────────────────────────────────────────────────────

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Turns valid rows into new CustomSale entries. On its own this is
 * "additive": the app already sums every entry sharing a date for display
 * and totals (see groupSalesByDate/getCustomSalesTotal), so appending these
 * to the existing list is sufficient to make an imported amount add to
 * whatever's already recorded for that date. See buildMergedSales for the
 * "overwrite" case, which additionally strips the entries these replace.
 */
export const buildImportedSales = (rows: ParsedImportRow[]): CustomSale[] =>
  rows
    .filter(
      (r): r is ParsedImportRow & { date: LocalDate; amount: number } =>
        !r.error && r.date !== null && r.amount !== null,
    )
    .map((r) => ({
      id: generateId(),
      title: "",
      amount: r.amount,
      date: r.date.toString(),
    }));

/** Whether an imported date's amount adds to what's already recorded, or
 * replaces it outright. */
export type ImportMode = "add" | "overwrite";

/**
 * Builds the full sales list to save, given the chosen conflict-handling mode.
 *  - "add"      : imported entries are appended; nothing existing is touched.
 *  - "overwrite": every existing entry whose date appears in the import is
 *    dropped first, so the imported amount becomes that date's only entry
 *    (or entries, if the CSV itself has several rows on the same date).
 */
export const buildMergedSales = (
  mode: ImportMode,
  existing: CustomSale[],
  rows: ParsedImportRow[],
): CustomSale[] => {
  const additions = buildImportedSales(rows);
  if (mode === "add") return [...existing, ...additions];

  const importedDates = new Set(additions.map((a) => a.date));
  const kept = existing.filter((s) => !importedDates.has(s.date));
  return [...kept, ...additions];
};
